/**
 * planTasksPartialStore — as tarefas do plano que chegam GRUPO A GRUPO enquanto
 * o motor ainda monta o plano (spec motor 2026-10-24, "tarefas do plano em
 * partes", §2.3).
 *
 * O motor manda um frame SSE `progress` com `step: 'plan_tasks_partial'` cada
 * vez que o densificador conclui um subsistema. Este store acumula esses grupos
 * por conversa + `researchKey`, SÓ EM MEMÓRIA: nada aqui é salvo, nada
 * sobrevive à recarga (spec §1, item 7). O dono do número final é o card do
 * plano; quando ele chega, ou o turno falha/termina, o acumulado é apagado
 * (`limpa`) — provisório e final nunca aparecem juntos.
 *
 * O frame não tem contrato publicado (spec §2.1), então a tela valida aqui, com
 * zod, e IGNORA o que não reconhece: frame torto não derruba a tela nem apaga o
 * que já estava acumulado.
 */
import { create } from 'zustand'
import { z } from 'zod'
import type { MaintenanceTaskV2 } from '../types/web-search'

export const PLAN_TASKS_PARTIAL_STEP = 'plan_tasks_partial'

const SKILLS = [
  'mechanical',
  'electrical',
  'instrumentation',
  'lubrication',
  'inspection',
  'general',
] as const

// `looseObject`: os campos que a tabela LÊ sem guarda são exigidos; o resto
// (proveniência, criticidade, citações por linha) passa intacto.
const tarefaSchema = z.looseObject({
  task: z.string().trim().min(1),
  procedure_steps: z.array(z.string()).optional(),
  period: z.looseObject({
    interval_value: z.number().optional(),
    interval_unit: z.enum(['HOURS', 'DAYS', 'WEEKS', 'MONTHS', 'YEARS', 'CYCLES']).optional(),
    display: z.string().optional(),
    band_label: z.string().optional(),
  }),
  technician: z.looseObject({ skill: z.enum(SKILLS), count: z.number() }),
  materials: z.array(z.looseObject({ name: z.string(), quantity: z.number(), unit: z.string() })),
  tools: z.array(z.looseObject({ name: z.string(), type: z.string() })),
  duration_minutes: z.number(),
})

const frameSchema = z.object({
  researchKey: z.string().trim().min(1),
  subsystem: z.string().trim().min(1),
  tasks: z.array(tarefaSchema),
  totalSoFar: z.number().optional(),
  target: z.number().int().positive(),
  sourceLocale: z.string().optional(),
})

export interface GrupoProvisorio {
  subsystem: string
  tasks: MaintenanceTaskV2[]
}

interface Acumulado {
  grupos: GrupoProvisorio[]
  titulos: string[]
  target: number
}

export interface TarefasProvisorias {
  grupos: GrupoProvisorio[]
  total: number
  target: number
}

interface PlanTasksPartialState {
  /** conversa → researchKey → acumulado */
  porConversa: Record<string, Record<string, Acumulado>>
  /** Devolve `false` quando o frame foi ignorado (malformado ou sem conversa). */
  recebe: (conversationId: string, data: unknown) => boolean
  limpa: (conversationId: string) => void
  /** Fim de turno: só existe um turno em voo por vez, então tudo o que é provisório sai. */
  limpaTudo: () => void
}

/** Mesma régua do motor (`normalizeTaskTitle`): acento, caixa e espaço não distinguem tarefa. */
export function normalizaTituloDaTarefa(titulo: string): string {
  return titulo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function acrescenta(
  anterior: Acumulado | undefined,
  subsystem: string,
  tasks: MaintenanceTaskV2[],
  target: number,
): Acumulado {
  const vistos = new Set(anterior?.titulos ?? [])
  const novas = tasks.filter((t) => {
    const chave = normalizaTituloDaTarefa(t.task)
    if (vistos.has(chave)) return false
    vistos.add(chave)
    return true
  })
  const grupos = anterior?.grupos ?? []
  if (novas.length === 0) return { grupos, titulos: [...vistos], target }
  const idx = grupos.findIndex((g) => g.subsystem === subsystem)
  const proximos =
    idx === -1
      ? [...grupos, { subsystem, tasks: novas }]
      : grupos.map((g, i) => (i === idx ? { subsystem, tasks: [...g.tasks, ...novas] } : g))
  return { grupos: proximos, titulos: [...vistos], target }
}

export const usePlanTasksPartialStore = create<PlanTasksPartialState>((set, get) => ({
  porConversa: {},
  recebe: (conversationId, data) => {
    if (!conversationId) return false
    const parsed = frameSchema.safeParse(data)
    if (!parsed.success) return false
    const { researchKey, subsystem, target } = parsed.data
    // O schema garante os campos que a tabela lê; o resto da linha é o do motor.
    const tasks = parsed.data.tasks as unknown as MaintenanceTaskV2[]
    const daConversa = get().porConversa[conversationId] ?? {}
    const proximo = acrescenta(daConversa[researchKey], subsystem, tasks, target)
    set((s) => ({
      porConversa: {
        ...s.porConversa,
        [conversationId]: { ...daConversa, [researchKey]: proximo },
      },
    }))
    return true
  },
  limpa: (conversationId) => {
    if (!get().porConversa[conversationId]) return
    set((s) => {
      const resto = { ...s.porConversa }
      delete resto[conversationId]
      return { porConversa: resto }
    })
  },
  limpaTudo: () => {
    if (Object.keys(get().porConversa).length > 0) set({ porConversa: {} })
  },
}))

/** O que a tela desenha para a conversa — `null` quando não há nada provisório. */
export function selecionaTarefasProvisorias(
  state: Pick<PlanTasksPartialState, 'porConversa'>,
  conversationId: string | null | undefined,
): TarefasProvisorias | null {
  if (!conversationId) return null
  const acumulados = Object.values(state.porConversa[conversationId] ?? {})
  const grupos = acumulados.flatMap((a) => a.grupos)
  if (grupos.length === 0) return null
  const total = grupos.reduce((n, g) => n + g.tasks.length, 0)
  const target = Math.max(...acumulados.map((a) => a.target))
  return { grupos, total, target }
}
